<template>
  <v-container>
    <toolbar :mode="mode" view="list" :icon="icon" :title="`${capitalize(pluralize(mode))}`" :count="items.count">
      <template v-if="!['application','agent'].includes(mode)">
        <ax-button icon="mdi-trash-can-outline" :disabled="!canDelete" confirm danger @confirmed="onDelete" />
      </template>
      <v-spacer />
      <template v-if="!['application','agent'].includes(mode)">
        <v-text-field v-model="search" label="Search" hide-details single-line clearable prepend-icon="mdi-magnify" @click:clear="search = null" dark></v-text-field>
      </template>
      <v-spacer />
      <small>Showing 1-{{items.results.length}} of {{items.count}}</small>
    </toolbar>
    <v-card flat>
      <v-data-table
        tile
        v-model="selected"
        :search="search"
        :items="items.results"
        :headers="headers"
        hide-default-footer
        disable-pagination
        :items-per-page="-1"
        must-sort
        :multi-sort="false"
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        :show-select="!['application', 'agent'].includes(mode)"
        @click:row="onRowClick"
        class="text-no-wrap hover"
      >
        <template v-slot:[`item.type`]="{ item }">
          <template v-if="'content_type' in item">
            <v-icon color="grey">{{ typeIcon(item.content_type) }}</v-icon>
          </template>
          <template v-else-if="mode == 'export'">
            {{ exportType(item) }}
          </template>
        </template>
        <template v-slot:[`item.name`]="{ item }">
          <template v-if="'content_object' in item">
            <template v-if="!!item.content_object">
              {{ item.content_object.name }}
            </template>
            <template v-else>
              Oops
            </template>
          </template>
          <template v-else>
            {{ item.name || item.uuid }}
          </template>
        </template>
        <template v-slot:[`item.application`]="{ item }">
          <template v-if="'application' in item">
            {{ item.application.name }}
          </template>
        </template>
        <template v-slot:[`item.prediction`]="{ item }">
          <template v-if="'prediction' in item">
            {{ item.prediction.name }}
          </template>
        </template>
        <template v-slot:[`item.dataset`]="{ item }">
          <template v-if="'dataset' in item">
            {{ item.dataset.name }}
          </template>
        </template>
        <template v-slot:[`item.files`]="{ item }">
          <template v-if="'completion' in item">
            {{ item.completion.name }}
          </template>
          <template v-if="'production' in item && item.production">/ {{ item.production.name }}</template>
          <template v-if="'economic' in item && item.economic">/ {{ item.economic.name }}</template>
          <template v-if="'file' in item && item.file && item.file.name">
            {{ item.file.name }}
          </template>
        </template>
        <template v-slot:[`item.shape`]="{ item }">
          <template v-if="item.shape">
            {{ item.shape[0] | numberFormat }}
          </template>
        </template>
        <template v-slot:[`item.status`]="{ item }">
          <ax-status :uuid="item.uuid" :status='item.status' />
        </template>
        <template v-slot:[`item.updated_at`]="{ item }">
          <span :title="item.updated_at | dateFormat('MMM d, YYYY')">{{ item.updated_at | dateFormat }}</span>
        </template>
        <template v-slot:[`item.owner`]="{ item }">
          {{ item.owner.name }}
        </template>
        <template v-slot:[`item.result`]="{ item }">
          <template v-if="item.result">
            <ax-status v-if="!!item.content_object" :uuid="item.content_object.uuid" :status="item.result.status" />
            <ax-status v-else :status="item.result.status" />
          </template>
          <template v-else>
            <ax-status />
          </template>
        </template>
        <template v-slot:[`item.task`]="{ item }">
          <template v-if="item.result">
            {{ item.result.task_name }}
          </template>
          <template v-else>
            {{ item.uuid }}
          </template>
        </template>
        <template v-slot:[`item.time_taken`]="{ item }">
          <template v-if="item.result">
            {{ date_diff(item.updated_at, item.created_at) }}
          </template>
        </template>
      </v-data-table>
    </v-card>
    <span v-intersect="onIntersect"></span>
  </v-container>
</template>
<script>
import toolbar from "@/components/toolbar"
import AxButton from "@/components/button"
import AxToggle from "@/components/toggle"
import AxStatus from "@/components/status"
export default {
  name: "List",
  components: {
    toolbar,
    AxButton,
    AxToggle,
    AxStatus
  },
  props: {
    items: { type: Object, required: true },
    mode: { type: String, required: true },
  },
  data() {
    return {
      selected: [],
      sortBy: "updated_at",
      sortDesc: true,
      search: "",
      headers: [
        { text: "Status", value: "status", width: 1 },
        { text: "Type", value: "type", width: 1 },
        { text: "Name", value: "name" },
        { text: "Description", value: "description", width: 1 },
        { text: "File(s)", value: "files", width: 1 },
        { text: "Dataset", value: "dataset", width: 1 },
        { text: "Application", value: "application", width: 1 },
        { text: "Prediction", value: "prediction", width: 1 },
        { text: "Wells", value: "shape", width: 1 },
        { text: "Owner", value: "owner", width: 1 },
        { text: "Status", value: "result", width: 1 },
        { text: "Users", value: "users", width: 1 },
        { text: "Time Taken", value: "time_taken", width: 1 },
        { text: "Last updated", value: "updated_at", width: 1 },
      ]
    }
  },
  mounted() {
    if (this.mode == "file") {
      this.headers = this.headers.filter(h => ["status", "name", "shape", "owner", "updated_at"].includes(h.value))
    } else if (this.mode == "dataset") {
      this.headers = this.headers.filter(h => ["status", "name", "files", "shape", "owner", "updated_at"].includes(h.value))
    } else if (this.mode == "prediction") {
      this.headers = this.headers.filter(h => ["status", "name", "shape", "dataset", "application", "owner", "updated_at"].includes(h.value))
    } else if (this.mode == "holdback") {
      this.headers = this.headers.filter(h => ["status", "name", "shape", "updated_at"].includes(h.value))
    } else if (this.mode == "export") {
      // Built explicitly (not via filter()) so Type lands right next to
      // Prediction -- filter() would keep Type in its master-array position
      // (right after Status), not where this mode wants it.
      this.headers = ["status", "name", "type", "prediction", "owner", "updated_at"]
        .map(v => this.headers.find(h => h.value == v))
    } else if (this.mode == "report") {
      this.headers = this.headers.filter(h => ["status", "name", "owner", "updated_at"].includes(h.value))
    }
  },
  watch: {
    selected: function() {
      this.$emit("selected", this.selected)
    }
  },
  computed: {
    icon() {
      if (this.mode == "file") return "mdi-file-document"
      if (this.mode == "dataset") return "mdi-database"
      if (this.mode == "prediction") return "mdi-finance"
      if (this.mode == "holdback") return "mdi-chart-gantt"
      if (this.mode == "export") return "mdi-file-export-outline"
      if (this.mode == "report") return "mdi-file-chart"
      if (this.mode == "agent") return "mdi-creation"
      return "mdi-apps"
    },
    show() {
      return `${this.mode}Show`
    },
    create() {
      return `${this.mode}Create`
    },
    button_text() {
      if (this.mode == "file") return "Upload"
      if (this.mode == "dataset") return "Compile"
      if (this.mode == "prediction") return "Generate"
      if (this.mode == "export") return "Generate"
    },
    canCopy() {
      if (this.selected.length > 0) {
        return this.selected.every(e => e.owner.id == this.user.id)
      }
      return false
    },
    canDelete() {
      if (this.selected.length > 0) {
        return this.selected.every(e => e.owner.id == this.user.id)
      }
      return false
    }
  },
  methods: {
    path(item) {
      if (["application", "agent"].includes(this.mode)) {
        return { name: this.show, params: { slug: item.slug } }
      } else {
        return { name: this.show, params: { uuid: item.uuid } }
      }
    },
    // result(result) {
    //   if (this.mode == "task") {
    //     var result = JSON.parse(result)
    //     if (typeof result === "object" && result !== null)
    //       if (Array.isArray(result["exc_message"])) return `${result["exc_type"]} ${result["exc_message"][0]}`
    //       else return result["exc_type"]
    //     return result
    //   }
    //   return null
    // },
    iconColor(item) {
      return "grey"
    },
    cardColor(item) {
      if (["FAILURE", "REVOKED"].includes(item.status)) return "red"
      if (["STARTED", "PENDING"].includes(item.status)) return "amber"
      return null
    },
    statusIcon(item) {
      if (item.status == "PENDING") return "mdi-alert"
      if (item.status == "SUCCESS") return "mdi-check"
      if (item.status == "ERROR") return "mdi-alert-circle"
      if (item.status == "FAILURE") return "mdi-alert-circle"
      return "mdi-alert"
    },
    exportType(item) {
      let format = item.config && item.config.format
      if (format == 'combocurve') return 'ComboCurve'
      if (format == 'aries') return 'ARIES'
      return 'Default'
    },
    typeIcon(item) {
      if (item.model == "file") return "mdi-file-document"
      if (item.model == "dataset") return "mdi-database"
      if (item.model == "prediction") return "mdi-finance"
      if (item.model == "holdback") return "mdi-chart-gantt"
      if (item.model == "export") return "mdi-file-export-outline"
    },
    statusColor(item) {
      if (item.status == "PENDING") return "amber"
      if (item.status == "SUCCESS") return "green"
      if (item.status == "ERROR") return "red"
      if (item.status == "FAILURE") return "red"
      return "blue"
    },
    onIntersect(entries, observer) {
      if (entries[0].isIntersecting) {
        this.$emit("load")
      }
    },
    onRowClick(item) {
      if (!['PENDING','STARTED'].includes(item.status)) this.$router.push(this.path(item))
    },
    onDelete() {
      let items = this.selected.filter(e => this.isOwner(e)).map(e => e.uuid)
      this.$emit('delete', items)
    }
  }
}
</script>